import { useState, useEffect } from "react";
import { useAssessmentStore } from "../store/assessment.store";
import { useAssessment, useAssessmentStatus } from "../hooks/useAssessment";
import { ModernAssessmentInterface } from "../components/assessment/ModernAssessmentInterface";
import { CompletionScreen } from "../components/assessment/CompletionScreen";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/Button";
import { getDimensionName } from "../utils/dimensions";

export const AssessmentPage: React.FC = () => {
    const { status, currentQuestionIndex, questions, assessmentId, actions, result, answers } = useAssessmentStore();
    const { startAssessment, isStarting, isLoadingQuestions, questionsError, submitAnswer, isSubmitting, finalizeAssessment, isFinalizing } = useAssessment();
    const { data: assessmentStatus, isLoading: isLoadingStatus } = useAssessmentStatus();
    const [selectedValue, setSelectedValue] = useState<number | null>(null);
    const navigate = useNavigate();

    const currentQuestion = questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === questions.length - 1;

    // Efeito para sincronizar a resposta selecionada ao navegar
    useEffect(() => {
        if (currentQuestion) {
            const currentAnswer = answers.find(a => a.question_id === currentQuestion.id);
            const newSelectedValue = currentAnswer?.answer || null;
            setSelectedValue(newSelectedValue);
        }
    }, [currentQuestionIndex, currentQuestion?.id, answers]);

    // Removed automatic redirect - now handled manually in finalizationLogic

    const handleNext = () => {
        if (!assessmentId || selectedValue === null) return;
        
        const answer = { 
            question_id: currentQuestion.id, 
            answer: selectedValue
        };


        // Primeiro, salva a resposta no store local
        actions.answerQuestion(answer);

        const finalizationLogic = () => {
            finalizeAssessment(assessmentId, {
                onSuccess: (data) => {
                    actions.complete(data);
                    // Navigate to results with the assessment ID after showing completion
                    setTimeout(() => {
                        navigate(`/results/${assessmentId}`);
                    }, 2000);
                }
            });
        };

        if (isLastQuestion) {
            submitAnswer(assessmentId, answer, { onSuccess: finalizationLogic });
        } else {
            submitAnswer(assessmentId, answer);
        }
    };

    const handlePrevious = () => actions.previousQuestion();
    const handleStart = () => startAssessment();

    // ---- Renderização ----
    if (isLoadingQuestions || isLoadingStatus) return <div className="h-screen flex items-center justify-center"><p>Carregando...</p></div>;
    if (questionsError) return <div className="h-screen flex items-center justify-center"><p>Erro ao carregar: {questionsError.message}</p></div>;

    // Show completion message if user has already completed assessment
    if (assessmentStatus?.hasCompleted) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center space-y-6 max-w-md"
                >
                    <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto shadow-lg">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                            Avaliação Já Completada!
                        </h1>
                        <p className="text-lg text-slate-600">
                            Você já completou sua avaliação de comunicação. Cada usuário pode fazer apenas uma avaliação.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/results">
                            <Button className="w-full sm:w-auto">
                                Ver Meus Resultados
                            </Button>
                        </Link>
                        <Link to="/dashboard">
                            <Button variant="outline" className="w-full sm:w-auto">
                                Voltar ao Dashboard
                            </Button>
                        </Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    if (status === 'completed') {
        return <div className="h-screen flex items-center justify-center"><p>Redirecionando para os resultados...</p></div>;
    }

    // Show elegant message when user tries to start a second assessment
    if (status === 'already_completed') {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center space-y-6 max-w-lg bg-white rounded-2xl shadow-xl p-8"
                >
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-200 rounded-full flex items-center justify-center mx-auto shadow-lg">
                        <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                        </svg>
                    </div>
                    <div className="space-y-3">
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                            Uma Avaliação por Usuário
                        </h1>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            No momento, cada usuário pode realizar apenas <strong>uma avaliação de comunicação</strong>. 
                            Você já completou a sua!
                        </p>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                            <p className="text-sm text-amber-800">
                                <strong>📊 Boas notícias:</strong> Seus resultados estão salvos e você pode acessá-los a qualquer momento.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                        <Link to="/results">
                            <Button className="w-full sm:w-auto px-6 py-3">
                                Ver Meus Resultados
                            </Button>
                        </Link>
                        <Link to="/dashboard">
                            <Button variant="outline" className="w-full sm:w-auto px-6 py-3">
                                Voltar ao Dashboard
                            </Button>
                        </Link>
                    </div>
                    <div className="text-xs text-slate-500 pt-2">
                        Em breve: funcionalidades de reavaliação e acompanhamento de progresso
                    </div>
                </motion.div>
            </div>
        );
    }

    if (status === 'finalizing') {
        return <CompletionScreen />;
    }

    if (!currentQuestion) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center space-y-6"
                >
                    <div className="space-y-2">
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                            {assessmentStatus?.hasActive ? 'Continue Sua Avaliação' : 'Avaliação de Perfil de Comunicação'}
                        </h1>
                        <p className="text-lg text-slate-600">
                            {assessmentStatus?.hasActive 
                                ? `Você parou na questão ${assessmentStatus.activeAssessment?.Progress?.AnsweredQuestions || 0} de ${assessmentStatus.activeAssessment?.Progress?.TotalQuestions || 0}. Continue de onde parou.`
                                : 'Descubra seu estilo único de comunicação em apenas alguns minutos'
                            }
                        </p>
                        {assessmentStatus?.hasActive && (
                            <div className="pt-2">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                        className="bg-gradient-to-r from-primary-400 to-primary-500 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${assessmentStatus.activeAssessment?.Progress?.CompletionPercent || 0}%` }}
                                    />
                                </div>
                                <p className="text-sm text-slate-500 mt-1">
                                    {assessmentStatus.activeAssessment?.Progress?.CompletionPercent || 0}% completo
                                </p>
                            </div>
                        )}
                    </div>
                    <motion.button
                        onClick={handleStart}
                        disabled={isStarting}
                        className="px-8 py-4 bg-primary hover:bg-primary-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {isStarting ? 'Carregando...' : assessmentStatus?.hasActive ? 'Continuar' : 'Começar Agora'}
                    </motion.button>
                </motion.div>
            </div>
        );
    }

    return (
        <ModernAssessmentInterface
            questions={questions}
            currentQuestionIndex={currentQuestionIndex}
            selectedValue={selectedValue}
            onSelectAnswer={setSelectedValue}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isNextDisabled={selectedValue === null}
            isPreviousDisabled={currentQuestionIndex === 0 || isSubmitting || isFinalizing}
            isSubmitting={isSubmitting || isFinalizing}
            isLastQuestion={isLastQuestion}
            themeName={getDimensionName(currentQuestion.dimensao)}
        />
    );
};